/**
 * React binding for the OBD session.
 *
 * Deliberately thin. Connection state, discovery results and the trace log go
 * through React because they change rarely; signal values never do.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import {
  ObdSession, ObdSimulator, type CycleName, type DrivingStyle, type FuelInference,
  type SessionStatus, type Transport, type TripRecord, type VehicleProfile,
} from '@obd/core';
import { WebBluetoothTransport, canRememberAdapters, rememberedAdapters } from '../transport/webBluetooth';
import { useGpsFeed, type GpsStatus } from './location';

export interface TraceEntry {
  id: number;
  direction: 'tx' | 'rx' | 'info';
  text: string;
  t: number;
}

export interface SimulatorControls {
  style: DrivingStyle;
  cycle: CycleName;
  fuelType: 'petrol' | 'diesel';
}

interface SessionContextValue {
  session: ObdSession;
  status: SessionStatus;
  connecting: boolean;
  /** Non-zero while an automatic reconnection is in flight. */
  reconnectAttempt: number;
  error: string | null;
  trace: TraceEntry[];
  fuelInference: FuelInference | null;
  /** Non-null while the simulator is the active transport. */
  simulator: ObdSimulator | null;
  connectBluetooth: (acceptAll?: boolean) => Promise<void>;
  connectSimulator: (opts: Partial<SimulatorControls>) => Promise<void>;
  disconnect: () => Promise<void>;
  clearTrace: () => void;
  setVehicle: (v: VehicleProfile) => void;
  /** True once a Bluetooth device has been chosen, so reconnect needs no tap. */
  canReconnect: boolean;
  gps: GpsStatus;
  /** How connecting on its own is going. */
  autoConnect: AutoConnectStatus;
  /** The drive that most recently ended, for whoever is logging them. */
  lastTrip: TripRecord | null;
}

export interface AutoConnectStatus {
  /** Whether the browser can hand back the adapter after the app is reopened. */
  supported: boolean;
  /** Turned on in Setup. */
  enabled: boolean;
  /** The adapter the browser remembers, if any. */
  deviceName: string | null;
  /** True while a connection attempt is in flight. */
  searching: boolean;
  /** Failed attempts since the last success. */
  attempts: number;
}

const Ctx = createContext<SessionContextValue | null>(null);

const MAX_TRACE = 400;
const MAX_RECONNECT_ATTEMPTS = 8;
/** First wait before trying the remembered adapter again, in ms. */
const AUTO_BASE_MS = 3000;
const AUTO_MAX_MS = 30_000;

export function SessionProvider({
  children, vehicle, autoReconnect = true, autoConnect = false, gpsEnabled = false, onVehicleChange,
}: {
  children: ReactNode;
  vehicle: VehicleProfile;
  autoReconnect?: boolean;
  /** Connect to the remembered adapter on its own, whenever it is in range. */
  autoConnect?: boolean;
  /** Feed the phone's location into the session. */
  gpsEnabled?: boolean;
  /** Called when the session changes the profile itself, so it can be saved. */
  onVehicleChange?: (v: VehicleProfile) => void;
}): JSX.Element {
  const [status, setStatus] = useState<SessionStatus>(() => ({
    state: 'disconnected',
    scheduler: { requestsPerSec: 0, valuesPerSec: 0, latencyMs: 0, errorRate: 0, activePids: 0, batching: false },
    supportedPidCount: 0,
    ecuCount: 1,
  }));
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trace, setTrace] = useState<TraceEntry[]>([]);
  const [fuelInference, setFuelInference] = useState<FuelInference | null>(null);
  const [simulator, setSimulator] = useState<ObdSimulator | null>(null);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [lastTrip, setLastTrip] = useState<TripRecord | null>(null);
  const [autoStatus, setAutoStatus] = useState<AutoConnectStatus>(() => ({
    supported: canRememberAdapters(), enabled: autoConnect, deviceName: null, searching: false, attempts: 0,
  }));

  const bleRef = useRef<WebBluetoothTransport | null>(null);
  const autoRef = useRef(autoReconnect);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Set while the user asked to disconnect, so we do not fight them. */
  const intentionalStop = useRef(false);

  autoRef.current = autoReconnect;

  // Held in a ref so the session, which is created once, always calls the
  // current handler rather than the one captured at mount.
  const vehicleChangeRef = useRef(onVehicleChange);
  vehicleChangeRef.current = onVehicleChange;

  const traceId = useRef(0);
  const pushTrace = useCallback((direction: TraceEntry['direction'], text: string) => {
    setTrace((prev) => {
      const next = [...prev, { id: traceId.current++, direction, text, t: Date.now() }];
      return next.length > MAX_TRACE ? next.slice(next.length - MAX_TRACE) : next;
    });
  }, []);

  const session = useMemo(
    () =>
      new ObdSession(vehicle, {
        onStateChange: (state, detail) => {
          if (detail) pushTrace('info', detail);
          if (state === 'error' && detail) setError(detail);
          if (state === 'connected') setError(null);
        },
        onTrace: (direction, text) => pushTrace(direction, text),
        onFuelInference: (inf) => setFuelInference(inf),
        onVehicleChange: (v) => vehicleChangeRef.current?.(v),
        onTripComplete: (trip) => setLastTrip(trip),
        onError: (err) => pushTrace('info', 'Error: ' + err.message),
      }),
    // One session for the lifetime of the app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Poll the status object rather than pushing it: scheduler throughput changes
  // continuously and a status bar refreshing twice a second is plenty.
  useEffect(() => {
    const id = setInterval(() => setStatus(session.status()), 400);
    return () => clearInterval(id);
  }, [session]);

  const attach = useCallback(
    async (transport: Transport) => {
      setConnecting(true);
      setError(null);
      try {
        await session.connect(transport);
        setStatus(session.status());
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        // A cancelled device chooser is a normal outcome, not a failure.
        if (!/cancell?ed|User cancelled|chooser/i.test(message)) setError(message);
        pushTrace('info', 'Connection failed: ' + message);
        throw err;
      } finally {
        setConnecting(false);
      }
    },
    [session, pushTrace],
  );

  /**
   * Recover a dropped Bluetooth link on our own.
   *
   * A cheap BLE dongle drops fairly regularly -- a bump in the socket, a
   * momentary brownout when cranking -- and a dashboard that needs the driver
   * to tap through a chooser at that moment is worse than useless. Backoff is
   * exponential and capped so a genuinely absent adapter is not hammered.
   */
  const scheduleReconnect = useCallback(() => {
    const transport = bleRef.current;
    if (!autoRef.current || intentionalStop.current || !transport?.hasDevice) return;
    if (retryTimer.current !== null) return;

    setReconnectAttempt((prev) => {
      const attempt = prev + 1;
      if (attempt > MAX_RECONNECT_ATTEMPTS) {
        pushTrace('info', 'Giving up after ' + MAX_RECONNECT_ATTEMPTS + ' reconnection attempts.');
        return 0;
      }
      const delay = Math.min(1000 * 2 ** (attempt - 1), 15000);
      pushTrace('info', 'Link lost — retrying in ' + Math.round(delay / 1000) + 's (attempt ' + attempt + ').');

      retryTimer.current = setTimeout(() => {
        retryTimer.current = null;
        void (async () => {
          try {
            await session.connect(transport);
            setReconnectAttempt(0);
            pushTrace('info', 'Reconnected.');
          } catch {
            scheduleReconnect();
          }
        })();
      }, delay);

      return attempt;
    });
  }, [session, pushTrace]);

  const connectBluetooth = useCallback(
    async (acceptAll = false) => {
      setSimulator(null);
      intentionalStop.current = false;
      setReconnectAttempt(0);
      const transport = new WebBluetoothTransport({
        acceptAll,
        onLog: (msg) => pushTrace('info', msg),
      });
      bleRef.current = transport;
      transport.onClose(() => scheduleReconnect());
      await attach(transport);
    },
    [attach, pushTrace, scheduleReconnect],
  );

  const connectSimulator = useCallback(
    async (opts: Partial<SimulatorControls>) => {
      intentionalStop.current = false;
      const sim = new ObdSimulator({
        fuelType: opts.fuelType ?? 'petrol',
        style: opts.style ?? 'normal',
        cycle: opts.cycle ?? 'mixed',
        latencyMs: 24,
        jitterMs: 8,
        // Start a few seconds before the first pull-away rather than from a
        // long wait at rest -- long enough to get from Setup to a dashboard,
        // because pulling away and changing up is what lets the gear
        // indicator number the gears.
        startPhase: 12,
      });
      setSimulator(sim);
      await attach(sim);
    },
    [attach],
  );

  /**
   * Connect to the adapter the browser remembers, without a chooser.
   *
   * This is what makes "get in the car, open the app, it is already logging"
   * work. It can only run while the app is open: a web page cannot be woken
   * by a Bluetooth device in the background, on any browser.
   */
  const connectRemembered = useCallback(async (): Promise<'connected' | 'no-device'> => {
    const devices = await rememberedAdapters();
    const device = devices[0];
    setAutoStatus((s) => ({ ...s, deviceName: device?.name ?? null }));
    if (!device) return 'no-device';

    setSimulator(null);
    const transport = new WebBluetoothTransport({ device, onLog: (msg) => pushTrace('info', msg) });
    bleRef.current = transport;
    transport.onClose(() => scheduleReconnect());
    await attach(transport);
    return 'connected';
  }, [attach, pushTrace, scheduleReconnect]);

  const connected = status.state === 'connected';

  // Keep trying the remembered adapter, backing off, while there is nothing
  // else going on. Paused while the app is in the background, where a phone
  // would throttle the timer anyway.
  useEffect(() => {
    setAutoStatus((s) => (s.enabled === autoConnect ? s : { ...s, enabled: autoConnect }));
    if (!autoConnect || connected || connecting || simulator !== null || intentionalStop.current) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;

    const queue = (ms: number): void => {
      if (cancelled) return;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => void run(), ms);
    };

    const run = async (): Promise<void> => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        queue(AUTO_MAX_MS);
        return;
      }
      setAutoStatus((s) => ({ ...s, searching: true }));
      try {
        const outcome = await connectRemembered();
        if (cancelled) return;
        if (outcome === 'no-device') {
          // Nothing paired yet: there is nothing to retry until the driver
          // picks an adapter once.
          setAutoStatus((s) => ({ ...s, searching: false, attempts: 0 }));
          return;
        }
        attempts = 0;
      } catch {
        attempts++;
        queue(Math.min(AUTO_BASE_MS * 1.6 ** (attempts - 1), AUTO_MAX_MS));
      } finally {
        if (!cancelled) setAutoStatus((s) => ({ ...s, searching: false, attempts }));
      }
    };

    // A moment after opening, so the first paint is not competing with it.
    queue(600);

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') queue(300);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [autoConnect, connected, connecting, simulator, connectRemembered]);

  const disconnect = useCallback(async () => {
    intentionalStop.current = true;
    if (retryTimer.current !== null) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    setReconnectAttempt(0);
    await session.disconnect();
    bleRef.current?.forget();
    bleRef.current = null;
    setSimulator(null);
    setStatus(session.status());
  }, [session]);

  const setVehicle = useCallback((v: VehicleProfile) => session.setVehicle(v), [session]);
  const gps = useGpsFeed(session, gpsEnabled);

  const value = useMemo<SessionContextValue>(
    () => ({
      session, status, connecting, reconnectAttempt, error, trace, fuelInference, simulator,
      connectBluetooth, connectSimulator, disconnect,
      clearTrace: () => setTrace([]),
      setVehicle,
      canReconnect: bleRef.current?.hasDevice ?? false,
      gps,
      autoConnect: autoStatus,
      lastTrip,
    }),
    [session, status, connecting, reconnectAttempt, error, trace, fuelInference, simulator,
      connectBluetooth, connectSimulator, disconnect, setVehicle, gps, autoStatus, lastTrip],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession must be used inside a SessionProvider');
  return ctx;
}

/** Re-render when signals appear or disappear, but never on a value change. */
export function useSignalStructure(): number {
  const { session } = useSession();
  const [version, setVersion] = useState(0);
  useEffect(() => session.store.onStructureChange(() => setVersion((v) => v + 1)), [session]);
  return version;
}

/** Hold a screen wake lock while connected, if the browser offers one. */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;
    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async (): Promise<void> => {
      try {
        sentinel = await navigator.wakeLock.request('screen');
      } catch {
        // Denied or unsupported; the dashboard still works, the screen just sleeps.
      }
    };
    void acquire();

    // The lock is dropped whenever the tab is hidden, so retake it on return.
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible' && !cancelled) void acquire();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel?.release();
    };
  }, [active]);
}

// ---------------------------------------------------------------- installing

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallState {
  /** True when the browser has offered an install prompt we can trigger. */
  available: boolean;
  /** True when already running as an installed app. */
  installed: boolean;
  /** iOS has no prompt API; the user has to use Share > Add to Home Screen. */
  manualOnly: boolean;
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

/**
 * Whether we are running as an installed app rather than in a browser tab.
 *
 * Guarded because a failure here would otherwise take the whole app down, and
 * "am I installed" is never important enough to be worth that.
 */
function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  } catch {
    /* not available */
  }
  // iOS uses a non-standard flag rather than the display-mode media query.
  return (window.navigator as { standalone?: boolean }).standalone === true;
}

/**
 * Wraps the install flow.
 *
 * Chrome fires `beforeinstallprompt` once it considers the app installable,
 * and the event must be kept so the prompt can be shown from a later user
 * gesture. Every other browser needs the manual Share-sheet route instead.
 */
export function useInstallPrompt(): InstallState {
  const deferred = useRef<BeforeInstallPromptEvent | null>(null);
  const [available, setAvailable] = useState(false);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    const onPrompt = (e: Event): void => {
      e.preventDefault();
      deferred.current = e as BeforeInstallPromptEvent;
      setAvailable(true);
    };
    const onInstalled = (): void => {
      setInstalled(true);
      setAvailable(false);
      deferred.current = null;
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    const event = deferred.current;
    if (!event) return 'unavailable';
    await event.prompt();
    const { outcome } = await event.userChoice;
    deferred.current = null;
    setAvailable(false);
    return outcome;
  }, []);

  const manualOnly =
    !available && !installed && typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent);

  return { available, installed, manualOnly, install };
}

/**
 * Fullscreen, orientation lock and screen dimming for use in a phone stand.
 *
 * All three are best-effort: orientation lock is refused outside installed
 * apps on some browsers, and fullscreen needs a user gesture. Failing any of
 * them must not stop the dashboard working.
 */
export async function enterDriveMode(orientation: 'auto' | 'landscape' | 'portrait'): Promise<void> {
  try {
    if (!document.fullscreenElement) await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
  } catch {
    /* refused, or already fullscreen */
  }
  if (orientation === 'auto') return;
  try {
    const so = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
    await so.lock?.(orientation);
  } catch {
    /* locking is only permitted in some contexts; the app still works */
  }
}

export async function exitDriveMode(): Promise<void> {
  try {
    const so = screen.orientation as ScreenOrientation & { unlock?: () => void };
    so.unlock?.();
  } catch {
    /* nothing to unlock */
  }
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
  } catch {
    /* already exited */
  }
}
